import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, parseISO, startOfYear, endOfYear } from 'date-fns';

export interface DailyGoalHistoryEntry {
  date: string; // YYYY-MM-DD
  target: number;
  current: number;
  type: 'time' | 'pages' | 'chapters';
  isComplete: boolean;
}

interface UseDailyGoalHistoryOptions {
  year: number;
  month?: number; // 1-12, if not provided, fetches whole year
}

export function useDailyGoalHistory({ year, month }: UseDailyGoalHistoryOptions) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['daily-goal-history', user?.id, year, month],
    queryFn: async (): Promise<DailyGoalHistoryEntry[]> => {
      if (!user) return [];

      // Determine date range
      const startDate = month 
        ? startOfMonth(new Date(year, month - 1))
        : startOfYear(new Date(year, 0));
      const endDate = month
        ? endOfMonth(new Date(year, month - 1))
        : endOfYear(new Date(year, 11));

      // Get user's goal settings (current settings - we'll use these as the baseline)
      const { data: prefs } = await supabase
        .from('user_reading_preferences')
        .select('daily_goal_type, daily_goal_target')
        .eq('user_id', user.id)
        .maybeSingle();

      const goalType = (prefs?.daily_goal_type as 'time' | 'pages' | 'chapters') || 'time';
      const goalTarget = prefs?.daily_goal_target || 15;

      // Get all days in the range
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      const results: DailyGoalHistoryEntry[] = [];

      // Batch fetch data for the entire period
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      if (goalType === 'time') {
        // Fetch all focus sessions in the date range
        const { data: focusSessions } = await supabase
          .from('focus_sessions')
          .select('created_at, actual_duration_seconds')
          .eq('user_id', user.id)
          .eq('was_completed', true)
          .gte('created_at', `${startDateStr}T00:00:00`)
          .lte('created_at', `${endDateStr}T23:59:59`);

        // Group by date
        const sessionsByDate = new Map<string, number>();
        focusSessions?.forEach(session => {
          const date = format(parseISO(session.created_at), 'yyyy-MM-dd');
          const current = sessionsByDate.get(date) || 0;
          sessionsByDate.set(date, current + Math.floor((session.actual_duration_seconds || 0) / 60));
        });

        // Build results
        for (const day of days) {
          const dateStr = format(day, 'yyyy-MM-dd');
          // Only include days up to today
          if (day > new Date()) continue;
          
          const current = sessionsByDate.get(dateStr) || 0;
          results.push({
            date: dateStr,
            target: goalTarget,
            current,
            type: goalType,
            isComplete: current >= goalTarget,
          });
        }
      } else if (goalType === 'pages') {
        // Fetch all reading progress activities in the date range
        const { data: activities } = await supabase
          .from('activities')
          .select('created_at, payload, book_id, type')
          .eq('user_id', user.id)
          .in('type', ['reading_progress_updated', 'book_finished'])
          .gte('created_at', `${startDateStr}T00:00:00`)
          .lte('created_at', `${endDateStr}T23:59:59`)
          .order('created_at', { ascending: true });

        // Calculate pages read per day
        const pagesByDate = new Map<string, number>();
        
        if (activities && activities.length > 0) {
          // Group activities by date and book
          const activitiesByDate = new Map<string, typeof activities>();
          activities.forEach(activity => {
            const date = format(parseISO(activity.created_at), 'yyyy-MM-dd');
            if (!activitiesByDate.has(date)) {
              activitiesByDate.set(date, []);
            }
            activitiesByDate.get(date)!.push(activity);
          });

          // For each date, calculate pages read
          for (const [date, dayActivities] of activitiesByDate) {
            const bookIds = [...new Set(dayActivities.map(a => a.book_id).filter(Boolean))] as string[];
            let totalPages = 0;

            for (const bookId of bookIds) {
              const bookActivities = dayActivities.filter(a => a.book_id === bookId);
              const latestActivity = bookActivities[bookActivities.length - 1];
              const latestPayload = latestActivity?.payload as Record<string, any> | null;
              
              let todayLatestPage = latestPayload?.current_page || 0;
              if (latestActivity?.type === 'book_finished' && !todayLatestPage && latestPayload?.total_pages) {
                todayLatestPage = latestPayload.total_pages;
              }

              // Get previous day's last page for this book
              const { data: previousActivity } = await supabase
                .from('activities')
                .select('payload')
                .eq('user_id', user.id)
                .eq('book_id', bookId)
                .in('type', ['reading_progress_updated', 'book_finished'])
                .lt('created_at', `${date}T00:00:00`)
                .order('created_at', { ascending: false })
                .limit(1)
                .maybeSingle();

              const previousPayload = previousActivity?.payload as Record<string, any> | null;
              const previousPage = previousPayload?.current_page || 0;

              totalPages += Math.max(0, todayLatestPage - previousPage);
            }

            pagesByDate.set(date, totalPages);
          }
        }

        // Build results
        for (const day of days) {
          const dateStr = format(day, 'yyyy-MM-dd');
          if (day > new Date()) continue;
          
          const current = pagesByDate.get(dateStr) || 0;
          results.push({
            date: dateStr,
            target: goalTarget,
            current,
            type: goalType,
            isComplete: current >= goalTarget,
          });
        }
      } else if (goalType === 'chapters') {
        // Fetch books finished per day
        const { data: finishedActivities } = await supabase
          .from('activities')
          .select('created_at')
          .eq('user_id', user.id)
          .eq('type', 'book_finished')
          .gte('created_at', `${startDateStr}T00:00:00`)
          .lte('created_at', `${endDateStr}T23:59:59`);

        // Group by date
        const chaptersByDate = new Map<string, number>();
        finishedActivities?.forEach(activity => {
          const date = format(parseISO(activity.created_at), 'yyyy-MM-dd');
          const current = chaptersByDate.get(date) || 0;
          chaptersByDate.set(date, current + 1);
        });

        // Build results
        for (const day of days) {
          const dateStr = format(day, 'yyyy-MM-dd');
          if (day > new Date()) continue;
          
          const current = chaptersByDate.get(dateStr) || 0;
          results.push({
            date: dateStr,
            target: goalTarget,
            current,
            type: goalType,
            isComplete: current >= goalTarget,
          });
        }
      }

      return results;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
